import React, { useEffect, useMemo, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { navigate } from '@reach/router';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import GetApp from '@mui/icons-material/GetApp';
import Search from '@mui/icons-material/Search';
import { toast } from 'react-toastify';
import ILocalAGGrid, {
  getDefaultColDef,
  withClickableCell,
} from '../../grid/iLocalAGGrid';
import {
  displaySafeText,
  fetchAdminFeatureLeads,
  getProductLicensingPath,
  setAdminEntitlement,
} from '../../../utils/productLicensing';
import '../feature-purchases/feature-purchases.css';

const ALL_FEATURES = '__all__';
const STATUS_OPEN = 'open';
const STATUS_ENABLED = 'enabled';
const STATUS_ALL = 'all';

const formatWhen = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch (_err) {
    return String(value);
  }
};

const csvEscape = (value) => `"${String(value == null ? '' : value).replace(/"/g, '""')}"`;

const StatusCell = (params) => {
  const enabled = params.data && params.data.status === STATUS_ENABLED;
  return (
    <span
      className={`feature-card-status ${enabled ? 'is-enabled' : 'is-open'}`}
    >
      {params.value}
    </span>
  );
};

const RequestActionsCell = (params) => {
  const row = params.data || {};
  const isOpen = row.status === STATUS_OPEN;
  const t = params.t;
  const enableLabel =
    (typeof t === 'function' &&
      t('FeatureRequests.Enable', { defaultValue: 'Enable' })) ||
    'Enable';
  const openLabel =
    (typeof t === 'function' &&
      t('FeatureRequests.Open', { defaultValue: 'Open' })) ||
    'Open';
  return (
    <span className="feature-requests-actions">
      {isOpen ? (
        <button
          type="button"
          className="feature-requests-enable-btn"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (typeof params.onEnable === 'function') {
              params.onEnable(row);
            }
          }}
        >
          {enableLabel}
        </button>
      ) : null}
      <button
        type="button"
        className="feature-requests-open-btn"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (typeof params.onOpen === 'function') {
            params.onOpen(row);
          }
        }}
      >
        {openLabel}
      </button>
    </span>
  );
};

const FeatureRequestsReport = ({ t }) => {
  const isIlocalUser = localStorage.getItem('isIlocalUser') === 'true';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [features, setFeatures] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [featureFilter, setFeatureFilter] = useState(ALL_FEATURES);
  const [statusFilter, setStatusFilter] = useState(STATUS_ALL);
  const [pendingEnable, setPendingEnable] = useState(null);
  const [enabling, setEnabling] = useState(false);

  const openLabel = t('LicensingAdmin.LeadOpen', {
    defaultValue: 'Awaiting follow-up',
  });
  const enabledLabel = t('LicensingAdmin.LeadEnabled', {
    defaultValue: 'Enabled',
  });

  const featureOptions = useMemo(
    () => [
      {
        id: ALL_FEATURES,
        name: t('FeaturePurchases.AllFeatures', { defaultValue: 'All features' }),
      },
      ...(features || [])
        .map((feature) => ({
          id: feature.id,
          name: displaySafeText(feature.name, ''),
        }))
        .filter((feature) => feature.name),
    ],
    [features, t]
  );

  const statusOptions = useMemo(
    () => [
      {
        id: STATUS_ALL,
        name: t('FeaturePurchases.AllStatuses', { defaultValue: 'All statuses' }),
      },
      {
        id: STATUS_OPEN,
        name: openLabel,
      },
      {
        id: STATUS_ENABLED,
        name: enabledLabel,
      },
    ],
    [enabledLabel, openLabel, t]
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await fetchAdminFeatureLeads({ status: STATUS_ALL });
        if (!cancelled) {
          setRows((data && data.leads) || []);
          setFeatures((data && data.features) || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            (err && err.message) ||
              t('FeatureRequests.LoadError', {
                defaultValue: 'Unable to load feature requests right now.',
              })
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    if (isIlocalUser) {
      load();
    } else {
      setLoading(false);
    }
    return () => {
      cancelled = true;
    };
  }, [isIlocalUser, t]);

  const filteredRows = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    return (rows || []).filter((row) => {
      if (statusFilter !== STATUS_ALL && row.status !== statusFilter) {
        return false;
      }
      if (featureFilter !== ALL_FEATURES && row.feature_key !== featureFilter) {
        return false;
      }
      if (!query) {
        return true;
      }
      const haystack = [
        row.account_name,
        row.feature_title,
        row.requested_by,
        row.contact_email,
        row.location_name,
        row.notes,
      ]
        .map((value) => String(value || '').toLowerCase())
        .join(' ');
      return haystack.indexOf(query) !== -1;
    });
  }, [rows, searchText, featureFilter, statusFilter]);

  const openCount = filteredRows.filter((row) => row.status === STATUS_OPEN)
    .length;
  const accountCount = new Set(
    filteredRows.map((row) => row.account_id || row.account_name)
  ).size;

  const selectedFeature =
    featureOptions.find((item) => item.id === featureFilter) || featureOptions[0];
  const selectedStatus =
    statusOptions.find((item) => item.id === statusFilter) || statusOptions[0];

  const statusLabelFor = (status) => {
    if (status === STATUS_OPEN) return openLabel;
    if (status === STATUS_ENABLED) return enabledLabel;
    return displaySafeText(status, '—');
  };

  const openLicensing = (row) => {
    if (!row || !row.account_id) return;
    navigate(
      getProductLicensingPath({
        accountId: row.account_id,
        featureKey: row.feature_key,
      })
    );
  };

  const handleConfirmEnable = async () => {
    if (!pendingEnable || enabling) return;
    setEnabling(true);
    try {
      await setAdminEntitlement({
        account_id: pendingEnable.account_id,
        feature_key: pendingEnable.feature_key,
        enabled: true,
        lead_id: pendingEnable.id,
      });
      toast.success(
        t('FeatureRequests.EnableSuccess', {
          defaultValue: 'Feature enabled for this account.',
        })
      );
      setPendingEnable(null);
      const data = await fetchAdminFeatureLeads({ status: STATUS_ALL });
      setRows((data && data.leads) || []);
      setFeatures((data && data.features) || []);
    } catch (err) {
      toast.error(
        (err && err.message) ||
          t('LicensingAdmin.SaveError', {
            defaultValue: 'Unable to update this feature.',
          })
      );
    } finally {
      setEnabling(false);
    }
  };

  const columnDefs = useMemo(
    () => [
      {
        field: 'requested_by',
        colId: 'requested_by',
        headerName: t('FeatureRequests.RequestedBy', {
          defaultValue: 'Requested by',
        }),
        valueGetter: (params) =>
          displaySafeText(params.data && params.data.requested_by, '—'),
        minWidth: 160,
      },
      {
        field: 'feature_title',
        colId: 'feature_title',
        headerName: t('FeaturePurchases.Feature', { defaultValue: 'Feature' }),
        valueGetter: (params) =>
          displaySafeText(params.data && params.data.feature_title, '—'),
        minWidth: 170,
      },
      withClickableCell(
        {
          field: 'account_name',
          colId: 'account_name',
          headerName: t('FeaturePurchases.Account', { defaultValue: 'Account' }),
          valueGetter: (params) =>
            displaySafeText(params.data && params.data.account_name, '—'),
          minWidth: 170,
        },
        (_event, row) => openLicensing(row)
      ),
      {
        field: 'location_name',
        colId: 'location_name',
        headerName: t('FeatureRequests.Location', { defaultValue: 'Location' }),
        valueGetter: (params) =>
          displaySafeText(params.data && params.data.location_name, '—'),
        minWidth: 170,
      },
      {
        field: 'contact_email',
        colId: 'contact_email',
        headerName: t('Features.ContactEmail', { defaultValue: 'Contact' }),
        valueGetter: (params) =>
          displaySafeText(params.data && params.data.contact_email, '—'),
        minWidth: 170,
      },
      {
        field: 'status',
        colId: 'status',
        headerName: t('FeaturePurchases.Status', { defaultValue: 'Status' }),
        valueGetter: (params) => statusLabelFor(params.data && params.data.status),
        cellRenderer: StatusCell,
        minWidth: 150,
      },
      {
        field: 'created_on',
        colId: 'created_on',
        headerName: t('LicensingAdmin.When', { defaultValue: 'When' }),
        valueFormatter: (params) => formatWhen(params.value),
        minWidth: 170,
      },
      {
        field: 'notes',
        colId: 'notes',
        headerName: t('Features.Notes', { defaultValue: 'Notes' }),
        valueGetter: (params) =>
          displaySafeText(params.data && params.data.notes, '—'),
        minWidth: 180,
      },
      {
        colId: 'actions',
        field: 'actions',
        headerName: t('Location.Actions', { defaultValue: 'Actions' }),
        cellRenderer: RequestActionsCell,
        cellRendererParams: {
          t,
          onEnable: (row) => setPendingEnable(row),
          onOpen: openLicensing,
        },
        filter: false,
        sortable: false,
        suppressMenu: true,
        minWidth: 168,
        maxWidth: 210,
        flex: 0,
        pinned: 'right',
        cellClass: 'ilocal-ag-actions-col',
      },
    ],
    [enabledLabel, openLabel, t]
  );

  const handleExport = () => {
    const header = [
      t('FeatureRequests.RequestedBy', { defaultValue: 'Requested by' }),
      t('FeaturePurchases.Feature', { defaultValue: 'Feature' }),
      t('FeaturePurchases.Account', { defaultValue: 'Account' }),
      t('FeatureRequests.Location', { defaultValue: 'Location' }),
      t('Features.ContactEmail', { defaultValue: 'Contact' }),
      t('FeaturePurchases.Status', { defaultValue: 'Status' }),
      t('LicensingAdmin.When', { defaultValue: 'When' }),
      t('Features.Notes', { defaultValue: 'Notes' }),
    ];
    const lines = [
      header.map(csvEscape).join(','),
      ...filteredRows.map((row) =>
        [
          displaySafeText(row.requested_by, '—'),
          displaySafeText(row.feature_title, '—'),
          displaySafeText(row.account_name, '—'),
          displaySafeText(row.location_name, '—'),
          displaySafeText(row.contact_email, '—'),
          statusLabelFor(row.status),
          formatWhen(row.created_on),
          row.notes || '',
        ]
          .map(csvEscape)
          .join(',')
      ),
    ];
    const blob = new Blob([lines.join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'feature-requests.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!isIlocalUser) {
    return (
      <div className="feature-purchases">
        <p className="licensing-empty">
          {t('LicensingAdmin.Forbidden', {
            defaultValue: 'This page is only available to iLocal members.',
          })}
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="feature-purchases">
        <CircularProgress size={28} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="feature-purchases">
        <p className="licensing-empty">{error}</p>
      </div>
    );
  }

  return (
    <div className="feature-purchases">
      <div className="feature-purchases-hero">
        <p className="features-hero-eyebrow">
          {t('FeatureRequests.Eyebrow', { defaultValue: 'Talk to Expert' })}
        </p>
        <p className="features-intro">
          {t('FeatureRequests.Intro', {
            defaultValue:
              'See who requested each licensed feature, and for which pharmacy. Enablement is account-wide on Product Licensing after the offline sale and applies to all locations.',
          })}
        </p>
        <div className="features-hero-meta">
          <span className="is-open">
            {t('FeatureRequests.OpenCount', {
              defaultValue: '{{count}} awaiting follow-up',
              count: openCount,
            })}
          </span>
          <span>
            {t('FeaturePurchases.AccountCount', {
              defaultValue: '{{count}} accounts',
              count: accountCount,
            })}
          </span>
        </div>
      </div>

      <div className="feature-purchases-toolbar">
        <TextField
          className="feature-purchases-search"
          variant="outlined"
          size="small"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder={t('FeatureRequests.SearchPlaceholder', {
            defaultValue: 'Search requester, account, feature, or location...',
          })}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" sx={{ color: '#64748b' }} />
              </InputAdornment>
            ),
          }}
        />
        <Autocomplete
          className="feature-purchases-filter"
          options={featureOptions}
          value={selectedFeature}
          disableClearable
          getOptionLabel={(option) => (option && option.name) || ''}
          isOptionEqualToValue={(option, value) =>
            !!(option && value && option.id === value.id)
          }
          onChange={(_event, value) =>
            setFeatureFilter((value && value.id) || ALL_FEATURES)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={t('FeaturePurchases.Feature', { defaultValue: 'Feature' })}
            />
          )}
        />
        <Autocomplete
          className="feature-purchases-filter"
          options={statusOptions}
          value={selectedStatus}
          disableClearable
          getOptionLabel={(option) => (option && option.name) || ''}
          isOptionEqualToValue={(option, value) =>
            !!(option && value && option.id === value.id)
          }
          onChange={(_event, value) =>
            setStatusFilter((value && value.id) || STATUS_ALL)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={t('FeaturePurchases.Status', { defaultValue: 'Status' })}
            />
          )}
        />
        <Button
          className="feature-purchases-export"
          variant="contained"
          startIcon={<GetApp />}
          disabled={filteredRows.length === 0}
          onClick={handleExport}
          sx={{
            textTransform: 'none',
            backgroundColor: '#00a7e1',
            borderRadius: '10px',
            fontWeight: 600,
            boxShadow: '0 8px 18px rgba(0, 167, 225, 0.28)',
            '&:hover': { backgroundColor: '#0096cc' },
          }}
        >
          {t('FeaturePurchases.Export', { defaultValue: 'Export CSV' })}
        </Button>
      </div>

      <ILocalAGGrid
        className="ag-theme-alpine feature-purchases-grid"
        columnDefs={columnDefs}
        defaultColDef={getDefaultColDef()}
        rowModelType="clientSide"
        rowData={filteredRows}
        pagination={true}
        paginationPageSize={25}
        paginationPageSizeSelector={[10, 20, 25]}
        rowHeight={48}
        overlayNoRowsMessage={t('FeatureRequests.Empty', {
          defaultValue: 'No feature requests match this search.',
        })}
        getRowId={(params) => {
          const row = params.data || {};
          if (row.id) {
            return String(row.id);
          }
          return [
            row.account_id,
            row.feature_key,
            row.created_on,
            row.contact_email,
          ].join('|');
        }}
      />
      <Dialog
        open={!!pendingEnable}
        onClose={() => {
          if (!enabling) setPendingEnable(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('FeatureRequests.EnableTitle', {
            defaultValue: 'Enable this feature?',
          })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('FeatureRequests.EnableConfirm', {
              defaultValue:
                'Enable {{feature}} for {{account}}? This does not collect payment.',
              feature: displaySafeText(
                pendingEnable && pendingEnable.feature_title,
                pendingEnable && pendingEnable.feature_key
              ),
              account: displaySafeText(
                pendingEnable && pendingEnable.account_name,
                '—'
              ),
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPendingEnable(null)}
            disabled={enabling}
            sx={{ textTransform: 'none' }}
          >
            {t('FeatureRequests.Cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmEnable}
            disabled={enabling || !pendingEnable}
            sx={{
              textTransform: 'none',
              backgroundColor: '#00a7e1',
              '&:hover': { backgroundColor: '#0096cc' },
            }}
          >
            {enabling
              ? t('FeatureRequests.Enabling', { defaultValue: 'Enabling...' })
              : t('FeatureRequests.Enable', { defaultValue: 'Enable' })}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default withTranslation()(FeatureRequestsReport);



// WEBPACK FOOTER //
// ./src/components/reports/feature-requests/index.js